import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { disableBodyScroll, enableBodyScroll } from "../utils";

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({
  ignoreMobileResize: true,
  autoRefreshEvents: "visibilitychange,DOMContentLoaded,load"
});
const mm = gsap.matchMedia();
let isResizing = false;
let lastWindowWidth = window.innerWidth;

window.addEventListener("resize", () => {
  const currentWidth = window.innerWidth;
  if (currentWidth === lastWindowWidth) return;
  lastWindowWidth = currentWidth;

  isResizing = true;
  enableBodyScroll();
  clearTimeout(window._resizeTimeout);
  window._resizeTimeout = setTimeout(() => {
    isResizing = false;
    ScrollTrigger.refresh();
  }, 300);
});

function playSequence(el) {
  if (isResizing) return;

  disableBodyScroll();
  el.classList.remove("is-active");
  void el.offsetWidth;
  el.classList.add("is-active");

  const overlay = el.querySelector(".fade-seq-overlay");
  const main = el.querySelector(".fade-seq-main");
  const header = document.querySelector(".header.header--fade");

  const blurTextA = overlay?.querySelector(".blur-text");
  if (blurTextA) {
    blurTextA.querySelectorAll("span").forEach((span, i) => {
      span.classList.add("blur-start");
      span.style.animationDelay = `${i * 0.05}s`;
    });
  }

  if (overlay) {
    const onOverlayFadeOut = (e) => {
      if (e.animationName === "fadeOut") {
        el.classList.add("has-faded");
        overlay.removeEventListener("animationend", onOverlayFadeOut);
      }
    };
    overlay.addEventListener("animationend", onOverlayFadeOut);
  }

  if (main && header) {
    const onMainFadeIn = (e) => {
      if (e.animationName === "fadeIn") {
        const blurTextB = main.querySelector(".blur-text");
        let maxDelay = 0;

        if (blurTextB) {
          const spans = blurTextB.querySelectorAll("span");
          spans.forEach((span, i) => {
            span.classList.add("blur-start");
            const delay = i * 0.05;
            span.style.animationDelay = `${delay}s`;
            maxDelay = delay;
          });
        }

        const blurDuration = 0.5;
        const totalBlurTime = (maxDelay + blurDuration) * 1000;

        setTimeout(() => {
          const decoration = el.querySelector(".fade-seq-decoration");
          if (decoration) {
            decoration.classList.add("fade-seq-decoration--visible");
          }

          const header = document.querySelector(".header.header--fade");
          if (header) {
            header.classList.add("is-visible");
          }

          const scrollIndicator = document.querySelector(".scroll-indicator");
          if (scrollIndicator) {
            scrollIndicator.classList.add("is-visible");
          }

          enableBodyScroll();
        }, totalBlurTime);

        main.removeEventListener("animationend", onMainFadeIn);
      }
    };
    main.addEventListener("animationend", onMainFadeIn);
  }
}

function forceShowWithoutAnimation(el) {
  el.classList.add("has-faded", "is-active");

  const main = el.querySelector(".fade-seq-main");
  const header = document.querySelector(".header.header--fade");

  if (main) {
    main.style.opacity = 1;
    main.style.animation = "none";
  }

  const overlay = el.querySelector(".fade-seq-overlay");
  if (overlay) {
    overlay.style.display = "none";
  }

  document.body.classList.remove("is-locked");

  if (header) {
    header.classList.add("is-visible");
  }

  const decoration = el.querySelector(".fade-seq-decoration");
  if (decoration) {
    decoration.classList.add("fade-seq-decoration--visible");
  }

  const blurTextB = el.querySelector(".fade-seq-main .blur-text");
  if (blurTextB) {
    blurTextB.querySelectorAll("span").forEach((span) => {
      span.classList.add("blur-start");
      span.style.animation = "none";
      span.style.opacity = 1;
      span.style.color = "#fff";
    });
  }

  // 別ページから遷移した場合はボディスクロールを有効にする
  enableBodyScroll();
}

window.addEventListener("load", () => {
  // TOPページ訪問フラグをチェック
  const isFromOtherPage = sessionStorage.getItem("fromOtherPage") === "true";
  if (isFromOtherPage) {
    sessionStorage.removeItem("fromOtherPage");
  }

  setTimeout(() => {
    const fadeEls = document.querySelectorAll(".fade-sequence");
    const decorationGroups = document.querySelectorAll(
      ".fade-seq-decoration-group"
    );
    // 下層ページ（.fade-sequence が無い場合）は装飾ボタンを即時表示
    if (fadeEls.length === 0 && decorationGroups.length > 0) {
      decorationGroups.forEach((group) => {
        const decoration = group.querySelector(".fade-seq-decoration");
        if (decoration)
          decoration.classList.add("fade-seq-decoration--visible");
      });
    }
    const mm = gsap.matchMedia();
    mm.add(
      {
        isPC: "(min-width: 1024px)",
        isSP: "(max-width:1023px)",
      },
      (context) => {
        const { isSP, isPC } = context.conditions;
        // TOP用: .fade-sequence があるときの挙動
        fadeEls.forEach((el) => {
          if (window.scrollY !== 0) {
            // 別ページから遷移した場合: スクロールをリセットして直接アニメーション実行
            window.scrollTo(0, 0);
            // ScrollTriggerを使わずに直接アニメーションを実行
            setTimeout(() => {
              playSequence(el);
            }, 100); // 少し遅延を入れてスクロール完了を待つ
          } else {
            // 通常の読み込み: ScrollTriggerでアニメーション実行
            ScrollTrigger.create({
              trigger: el,
              start: "top 50%",
              once: true,
              onEnter: () => playSequence(el),
            });
          }

          const main = el.querySelector(".fade-seq-bg");
          if (main) {
            ScrollTrigger.create({
              trigger: el,
              start: "bottom top",
              onEnter: () => {
                main.style.position = "absolute";
              },
              onLeaveBack: () => {
                main.style.position = "fixed";
              },
            });
          }

          const header = document.querySelector(".header");
          ScrollTrigger.create({
            trigger: el,
            start: "bottom top",
            onEnter: () => {
              header.classList.add("no-before");
            },
            onLeaveBack: () => {
              header.classList.remove("no-before");
            },
          });
          // 装飾ボタングループ（TOPでは.fase-sequence内、下層ではグローバル）
          const decorationGroup =
            el.querySelector(".fade-seq-decoration-group") ||
            document.querySelector(".fade-seq-decoration-group");
          const decoration = decorationGroup?.querySelector(".fade-seq-decoration");
          if (isSP && decorationGroup) {
            ScrollTrigger.create({
              trigger: ".contact__section",
              start: "top bottom",
              onEnter: () => {
                decorationGroup.style.transition = "opacity 0.3s";
                decorationGroup.style.opacity = "0";
                decorationGroup.style.pointerEvents = "none";
              },
              onLeaveBack: () => {
                decorationGroup.style.transition = "opacity 0.3s";
                decorationGroup.style.opacity = "";
                decorationGroup.style.pointerEvents = "";
              }
            });
          }

          if (isPC && decoration) {
            ScrollTrigger.getAll().forEach((st) => {
              if (st.trigger === document.querySelector(".contact__section")) {
                st.kill();
              }
            });

            if (
              !decoration.classList.contains("fade-seq-decoration--visible")
            ) {
              if (window.scrollY === 0) {
                return;
              } else {
                decoration.classList.add("fade-seq-decoration--visible");
              }
            }

            decoration.addEventListener("animationend", () => {
              if (
                decoration.classList.contains("fade-seq-decoration--visible")
              ) {
                ScrollTrigger.getAll().forEach((st) => {
                  if (
                    st.trigger === document.querySelector(".contact__section")
                  ) {
                    st.kill();
                  }
                });
              }
            });
          }
        });

        // 下層ページ SP: .contact__section でフェードアウト
        if (isSP && fadeEls.length === 0 && decorationGroups.length > 0) {
          decorationGroups.forEach(group => {
            ScrollTrigger.create({
              trigger: ".contact__section",
              start: "top bottom",
              onEnter: () => {
                group.style.transition = "opacity 0.3s";
                group.style.opacity = "0";
                group.style.pointerEvents = "none";
              },
              onLeaveBack: () => {
                group.style.transition = "opacity 0.3s";
                group.style.opacity = "";
                group.style.pointerEvents = "";
              }
            });
          });
        }
      }
    );
  }, 100);

  document
    .getElementById("scroll-indicator")
    ?.addEventListener("click", function () {
      const target = document.getElementById("message");
      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
        });
      }
    });

  mm.add(
    {
      isPC: "(min-width: 1024px)",
      isSP: "(max-width: 1023px)",
    },
    (context) => {
      const { isPC, isSP } = context.conditions;

      if (isPC) {
        context.add(() => {
          const path = window.location.pathname.replace(/\/+$/, "/");
          const isContactPage = /\/(contact)\/?$/.test(path);
          if (isContactPage) {
            return;
          }

          const sections = document.querySelectorAll(".reason__content");
          if (sections.length === 0) return;

          sections.forEach((section, index) => {
            section.style.zIndex = sections.length + index;
          });
          const HOLD_FRAC = 0.25;

          function activate(activeSection) {
            const activeIndex = Array.from(sections).indexOf(activeSection);
            sections.forEach((section, i) => {
              section.classList.remove("-active", "-inactive");
              const img = section.querySelector(".reason__content-imgs");

              if (i < activeIndex - 1 || i > activeIndex + 1) {
                section.classList.add("-inactive");
                if (img)
                  img.style.clipPath =
                    "polygon(0% 110%, 100% 110%, 100% 110%, 0% 110%)";
                return;
              }
              if (i === activeIndex - 1 && img) {
                img.style.clipPath =
                  "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
              }
              if (i === activeIndex + 1 && img) {
                img.style.clipPath =
                  "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)";
              }
            });

            activeSection.classList.add("-active");
            const img = activeSection.querySelector(".reason__content-imgs");
            if (img) {
              img.style.clipPath =
                "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
            }
          }

          function initScrollTriggers() {
            sections.forEach((section, index) => {
              const currentImage = section.querySelector(
                "[data-parallax-image]"
              );
              const nextSection = sections[index + 1];
              const nextImage = nextSection?.querySelector(
                "[data-parallax-image]"
              );
              const nextClip = nextSection?.querySelector(
                ".reason__content-imgs"
              );

              if (index !== 0 && currentImage) {
                currentImage.style.transform = "translateY(10%)";
              }

              let ticking = false;
              ScrollTrigger.create({
                trigger: ".reason__container-pc",
                start: () => `top+=${window.innerHeight * index} top`,
                end: () => `top+=${window.innerHeight * (index + 1)} top`,
                scrub: true,
                onUpdate: (self) => {
                  if (!ticking) {
                    requestAnimationFrame(() => {
                      const progress = self.progress;
                      let progressAdj = progress;

                      if (index > 0) {
                        progressAdj = Math.max(
                          0,
                          Math.min(1, (progress - HOLD_FRAC) / (1 - HOLD_FRAC))
                        );
                      }
                      if (currentImage) {
                        const yCurr = gsap.utils.interpolate(0, -3, progress);
                        currentImage.style.transform = `translateY(${yCurr}%)`;
                      }
                      if (nextImage) {
                        const yNext = gsap.utils.interpolate(
                          10,
                          0,
                          progressAdj
                        );
                        nextImage.style.transform = `translateY(${yNext}%)`;
                      }
                      if (nextClip) {
                        const clipY = gsap.utils.interpolate(
                          100,
                          0,
                          progressAdj
                        );
                        gsap.set(nextClip, {
                          clipPath: `polygon(0% ${clipY}%, 100% ${clipY}%, 100% 100%, 0% 100%)`,
                          overwrite: true,
                        });
                      }
                      ticking = false;
                    });
                    ticking = true;
                  }
                },
              });

              const half = 0.5;
              const offset = index > 0 ? HOLD_FRAC : 0;

              gsap.timeline({
                scrollTrigger: {
                  trigger: ".reason__container-pc",
                  start: () =>
                    `top+=${
                      window.innerHeight * (index + 1) -
                      window.innerHeight * (half - offset)
                    } top`,
                  end: () =>
                    `top+=${
                      window.innerHeight * (index + 2) -
                      window.innerHeight * (half - offset)
                    } top`,
                  scrub: true,
                  onEnter: () => {
                    if (sections[index + 1]) activate(sections[index + 1]);
                  },
                  onEnterBack: () => {
                    if (sections[index + 1]) activate(sections[index + 1]);
                  },
                },
              });
            });

            gsap.timeline({
              scrollTrigger: {
                trigger: ".reason__container-pc",
                start: `top top`,
                end: `top+=${window.innerHeight * 0.5} top`,
                scrub: true,
                onEnterBack: () => {
                  activate(sections[0]);
                },
              },
            });
          }

          const savedScrollY = parseInt(
            sessionStorage.getItem("scrollY-loungeRange") || "0"
          );
          if (!isNaN(savedScrollY)) {
            window.scrollTo(0, savedScrollY);
          }

          setTimeout(() => {
            ScrollTrigger.refresh();

            let closestSection = sections[0];
            let minDistance = Infinity;

            sections.forEach((section) => {
              const rect = section.getBoundingClientRect();
              const distance = Math.abs(rect.top);
              if (distance < minDistance) {
                minDistance = distance;
                closestSection = section;
              }
            });

            activate(closestSection);
            initScrollTriggers();
          }, 100);

          window.addEventListener("beforeunload", () => {
            sessionStorage.setItem("scrollY-loungeRange", window.scrollY);
          });
        });
      }

      if (isSP) {
        context.add(() => {
          const wrapper = document.querySelector(".parallax-wrapper");
          if (!wrapper) return;
          const sections = wrapper.querySelectorAll(".parallax-section");
          const texts = wrapper.querySelectorAll(".fade-text");
          const textContainer = wrapper.querySelector(".text-container");
          const textInner = wrapper.querySelector(".text-inner");
          let activeIndex = -1;

          // 1. 初期設定
          gsap.set([textContainer, textInner], {
            height: "100svh",
            minHeight: "100svh",
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            y: 0,
            autoAlpha: 1,
            force3D: true,
          });

          texts.forEach((text) => {
            gsap.set(text, {
              position: "absolute",
              top: "50%",
              left: "50%",
              xPercent: -50,
              yPercent: -50,
              x: "20",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              textAlign: "left",
              autoAlpha: 0,
              force3D: true,
              willChange: "transform, opacity",
              rotationZ: 0.01,
              backfaceVisibility: "hidden",
              zIndex: 100
            });
          });

          // 2. 全体の固定（pin）
          ScrollTrigger.create({
            trigger: wrapper,
            start: "top top",
            end: "bottom bottom",
            pin: textContainer,
            pinSpacing: false,
            pinType: "fixed",
            anticipatePin: 0,
            invalidateOnRefresh: true,
            fastScrollEnd: true,
          });

          // 3. 各セクションの処理
          sections.forEach((section, i) => {
            const bg = section.querySelector(".parallax-bg");
            
            gsap.to(bg, {
              yPercent: 10,
              ease: "none",
              scrollTrigger: {
                trigger: section,
                scrub: 0.5,
                start: "top bottom",
                end: "bottom top"
              }
            });

            // テキスト切り替え判定
            ScrollTrigger.create({
              trigger: section,
              start: "top 50%",
              end: "bottom 50%",
              onEnter: () => showText(i),
              onEnterBack: () => showText(i),
              onLeave: () => {
                if (i !== sections.length - 1) {
                  gsap.to(texts[i], { autoAlpha: 0, duration: 0.2, overwrite: "auto" });
                }
              },
              onLeaveBack: () => {
                if (i !== 0) {
                  gsap.to(texts[i], { autoAlpha: 0, duration: 0.2, overwrite: "auto" });
                }
              }
            });
          });

          function showText(index) {
            if (index === activeIndex || index < 0 || index >= texts.length) return;
            activeIndex = index;

            texts.forEach((text, i) => {
              if (i === index) {
                gsap.to(text, {
                  autoAlpha: 1,
                  duration: 0.3,
                  ease: "none",
                  overwrite: "auto"
                });
              } else {
                gsap.to(text, {
                  autoAlpha: 0,
                  duration: 0.2,
                  overwrite: "auto"
                });
              }
            });
          }

          // 4. 画像読み込み後の初期化
          const images = document.querySelectorAll(".parallax-bg");
          Promise.all(Array.from(images).map(img => {
            return new Promise(resolve => {
              if (img.complete) resolve();
              else { img.onload = resolve; img.onerror = resolve; }
            });
          })).then(() => {
            setTimeout(() => {
              ScrollTrigger.refresh();
              let current = -1;
              const vCenter = window.innerHeight / 2;
              sections.forEach((s, i) => {
                const rect = s.getBoundingClientRect();
                if (rect.top <= vCenter && rect.bottom >= vCenter) {
                  current = i;
                }
              });
              if (current === -1 && sections[0].getBoundingClientRect().top > vCenter) {
                  current = 0;
              }
              if (current !== -1) {
                activeIndex = current;
                gsap.set(texts[current], { autoAlpha: 1 });
              }
            }, 500);
          });
        });
      }
    }
  );
});
